/**
 * What a saved filter OPENS IN (KPR-1641, KPE-051 ruling i11): the card in
 * view, the table's columns and the view mode, beside its query, sort and
 * grouping. keepr-api docs/SCHEMA.md § Saved filters "What it opens in" is
 * the contract:
 *
 * - `card`    the card in view, by id (`?card=` on the items page);
 * - `columns` the table's columns in order, `{ element: '#<id>' }` (an element
 *             BY ITS ID, so a rename never breaks it) or `{ system }` (the
 *             table layout's system columns), `frozen: true` optional;
 * - `view`    grid, table, board or worklist (`?view=`).
 *
 * The API serves each as null when unset, and judges them for the reader: a
 * card they may not read is null, a column whose element was removed or is
 * hidden from them is gone. A filter's columns are ITS OWN: applied, they ride
 * the address (`?cols=`, `ITEMS_VIEW_PARAMS`) and never write anyone's table
 * layout (components/items/cardTableModel is that layout's model).
 *
 * This file is the one place the columns change shape between the three
 * places they live: the saved filter (`#id`s), the address (`cols`, one
 * comma-separated token per column, `*` after a frozen one) and the table
 * model (element NAMES, which is what `resolveTableColumns` draws).
 */

import { SYSTEM_COLUMNS, MAX_FROZEN, columnKey } from '../components/items/cardTableModel';

/** The view modes a saved filter may open in (the API's list; the map is not one). */
export const SAVED_FILTER_VIEWS = Object.freeze(['grid', 'table', 'board', 'worklist']);

/** The most columns a filter keeps (the table layout's limit). */
export const MAX_SAVED_COLUMNS = 100;

const ELEMENT_REF_RE = /^#[a-z0-9]{8}$/;
const HEX24_RE = /^[0-9a-f]{24}$/i;
const FROZEN_MARK = '*';
const has = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key);

/**
 * Any list of saved columns, normalised as the API stores them: an element by
 * `#id` or a known system column, each once, frozen ones (at most MAX_FROZEN)
 * leading, at most MAX_SAVED_COLUMNS. Junk is dropped, never thrown. [] for
 * anything that is not a list.
 */
export function savedColumnsOf(columns) {
	if (!Array.isArray(columns)) return [];
	const seen = new Set();
	const out = [];
	columns.forEach((column) => {
		if (!column || typeof column !== 'object') return;
		const element = typeof column.element === 'string' ? column.element.trim() : '';
		const system = typeof column.system === 'string' ? column.system.trim() : '';
		let entry = null;
		if (element && !system && ELEMENT_REF_RE.test(element)) entry = { element };
		else if (system && !element && has(SYSTEM_COLUMNS, system)) entry = { system };
		if (!entry) return;
		const key = entry.element || `system:${entry.system}`;
		if (seen.has(key)) return;
		seen.add(key);
		out.push(column.frozen === true ? { ...entry, frozen: true } : entry);
	});
	const frozen = out.filter(column => column.frozen).slice(0, MAX_FROZEN);
	const loose = out.filter(column => !frozen.includes(column)).map(({ frozen: dropped, ...rest }) => rest);
	return [...frozen, ...loose].slice(0, MAX_SAVED_COLUMNS);
}

/** Saved columns as the address's `cols` value, or undefined for none (the param is then deleted). */
export function columnsParamOf(columns) {
	const list = savedColumnsOf(columns);
	if (!list.length) return undefined;
	return list.map(column => `${column.element || column.system}${column.frozen ? FROZEN_MARK : ''}`).join(',');
}

/** The address's `cols` value read back as saved columns ([] for none or a mangled one). */
export function columnsFromParam(param) {
	if (typeof param !== 'string' || !param.trim()) return [];
	return savedColumnsOf(param.split(',').map((token) => {
		const raw = token.trim();
		const frozen = raw.endsWith(FROZEN_MARK);
		const name = frozen ? raw.slice(0, -FROZEN_MARK.length) : raw;
		return name.startsWith('#') ? { element: name, frozen } : { system: name, frozen };
	}));
}

/**
 * Saved columns as the TABLE MODEL draws them (cardTableModel `resolveTableColumns`
 * takes `{ columns }`): an element by its NAME on this card, found by id
 * among `elementDefs` (the card's merged elements) — one the card no longer
 * has is dropped, never drawn blank. null when nothing is left, so the
 * caller falls back to the person's own table layout.
 */
export function tableColumnsFromSaved(columns, elementDefs) {
	const nameById = new Map((elementDefs || []).filter(def => def?.id && def?.name).map(def => [`#${def.id}`, def.name]));
	const out = [];
	savedColumnsOf(columns).forEach((column) => {
		const base = column.system ? { system: column.system } : (nameById.has(column.element) ? { element: nameById.get(column.element) } : null);
		if (base) out.push(column.frozen ? { ...base, frozen: true } : base);
	});
	return out.length ? out : null;
}

/**
 * The table's columns as a saved filter stores them: each element column by
 * its id (one whose element has none, or that the card no longer has, is left
 * out), each system column by name, frozen kept. Widths and a group's display
 * stay the person's layout's. null when nothing is left.
 */
export function savedColumnsFromTable(tableColumns, elementDefs) {
	const idByName = new Map((elementDefs || []).filter(def => def?.id && def?.name).map(def => [def.name, `#${def.id}`]));
	const seen = new Set();
	const out = [];
	(Array.isArray(tableColumns) ? tableColumns : []).forEach((column) => {
		if (!column) return;
		const key = columnKey(column);
		if (seen.has(key)) return;
		seen.add(key);
		let base = null;
		if (column.system && has(SYSTEM_COLUMNS, column.system)) base = { system: column.system };
		else if (column.element && idByName.has(column.element)) base = { element: idByName.get(column.element) };
		if (base) out.push(column.frozen ? { ...base, frozen: true } : base);
	});
	const list = savedColumnsOf(out);
	return list.length ? list : null;
}

/** A filter's view mode, or null. A board without one opens as the board (KPR-1395). */
export function savedFilterViewOf(filter) {
	if (SAVED_FILTER_VIEWS.includes(filter?.view)) return filter.view;
	return filter?.board?.by ? 'board' : null;
}

/**
 * The address patch (hooks/useItemsViewParams `update`) that applies what a
 * saved filter opens in, for CollectionItemsPage `applySavedFilter`'s ONE
 * update: `card`, `view` and `cols`, each only when the filter carries it, so
 * a filter without them leaves the reader's card, view and columns as they
 * were (`{}` for a filter saved before KPR-1641).
 */
export function savedFilterViewPatch(filter) {
	const patch = {};
	if (typeof filter?.card === 'string' && HEX24_RE.test(filter.card)) patch.card = filter.card.toLowerCase();
	const view = savedFilterViewOf(filter);
	if (view) patch.view = view;
	const cols = columnsParamOf(filter?.columns);
	if (cols) patch.cols = cols;
	return patch;
}

/**
 * What a save or an Update writes (KPR-1642 calls it): `{ card, columns, view }`
 * from the page as it stands — the card in view (`?card=`, or none), the view
 * mode (one a filter may open in, else none) and the table's columns (by id;
 * none when the table is not the card's own). Each is null when there is
 * nothing to store, which a PATCH reads as "clear it".
 */
export function savedFilterViewPayload({ card = null, view = null, tableColumns = null, elementDefs = null } = {}) {
	return {
		card: typeof card === 'string' && HEX24_RE.test(card) ? card.toLowerCase() : null,
		view: SAVED_FILTER_VIEWS.includes(view) ? view : null,
		columns: tableColumns ? savedColumnsFromTable(tableColumns, elementDefs) : null,
	};
}

/** A filter as the client reads it: card, columns and view always present, null when unset (an older API sends no key). */
export const withSavedFilterView = filter => (filter && typeof filter === 'object'
	? {
		...filter,
		card: typeof filter.card === 'string' && HEX24_RE.test(filter.card) ? filter.card.toLowerCase() : null,
		columns: savedColumnsOf(filter.columns).length ? savedColumnsOf(filter.columns) : null,
		view: SAVED_FILTER_VIEWS.includes(filter.view) ? filter.view : null,
	}
	: filter);

/**
 * The table layouts the page draws with an applied filter's own columns
 * (`?cols=`) standing in for its card's: the card's layout keeps its other
 * settings (its default order), and is never written. Columns that name
 * nothing the card still has leave the layouts as they are.
 */
export function withSavedColumns(tableLayouts, cardId, savedColumns, elementDefs) {
	if (!cardId || !savedColumnsOf(savedColumns).length) return tableLayouts;
	const columns = tableColumnsFromSaved(savedColumns, elementDefs);
	if (!columns) return tableLayouts;
	const own = tableLayouts?.[cardId];
	return { ...(tableLayouts || {}), [cardId]: { ...(own || { card_id: cardId, kind: 'table' }), body: { ...(own?.body || {}), columns } } };
}
